<template>
    <v-card elevation="0">
        <v-card-title class="d-flex justify-space-between align-center py-3">
            <div>
                <v-icon left color="primary">mdi-calendar-clock</v-icon>
                <span class="text-h6">Schedule View</span>
            </div>
            <div class="d-flex align-center">
                <!-- View Selector -->
                <v-btn-toggle v-model="currentView" mandatory dense class="mr-3">
                    <v-btn value="timeGridDay" small>
                        <v-icon small left>mdi-calendar-today</v-icon>
                        Day
                    </v-btn>
                    <v-btn value="timeGridWeek" small>
                        <v-icon small left>mdi-calendar-week</v-icon>
                        Week
                    </v-btn>
                </v-btn-toggle>

                <!-- Weekend Toggle -->
                <v-btn 
                    small 
                    outlined 
                    :color="showWeekends ? 'primary' : 'grey'"
                    @click="showWeekends = !showWeekends"
                    class="mr-3 text-none"
                >
                    <v-icon small left>{{ showWeekends ? 'mdi-calendar-check' : 'mdi-calendar-remove' }}</v-icon>
                    Weekends
                </v-btn>

                <!-- Event Overlap Mode Toggle -->
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                            small 
                            outlined 
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            class="mr-3 text-none"
                        >
                            <v-icon small left>mdi-view-grid-outline</v-icon>
                            Overlap: {{ eventOverlapModeLabel }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense>
                        <v-list-item @click="setEventOverlapMode('overlap')">
                            <v-list-item-icon>
                                <v-icon small :color="eventOverlapMode === 'overlap' ? 'primary' : 'grey'">
                                    {{ eventOverlapMode === 'overlap' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Overlap</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">Events overlap naturally (default)</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="setEventOverlapMode('stack')">
                            <v-list-item-icon>
                                <v-icon small :color="eventOverlapMode === 'stack' ? 'primary' : 'grey'">
                                    {{ eventOverlapMode === 'stack' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Stack</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">No overlapping, side-by-side</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="setEventOverlapMode('limit-2')">
                            <v-list-item-icon>
                                <v-icon small :color="eventOverlapMode === 'limit-2' ? 'primary' : 'grey'">
                                    {{ eventOverlapMode === 'limit-2' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Limit 2</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">Show max 2, +more link for rest</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="setEventOverlapMode('limit-3')">
                            <v-list-item-icon>
                                <v-icon small :color="eventOverlapMode === 'limit-3' ? 'primary' : 'grey'">
                                    {{ eventOverlapMode === 'limit-3' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Limit 3</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">Show max 3, +more link for rest</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item @click="setEventOverlapMode('limit-4')">
                            <v-list-item-icon>
                                <v-icon small :color="eventOverlapMode === 'limit-4' ? 'primary' : 'grey'">
                                    {{ eventOverlapMode === 'limit-4' ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>Limit 4</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">Show max 4, +more link for rest</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- Navigation -->
                <v-btn icon small @click="calendarPrev">
                    <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn text small @click="calendarToday" class="text-none mx-1">
                    Today
                </v-btn>
                <v-btn icon small @click="calendarNext">
                    <v-icon>mdi-chevron-right</v-icon>
                </v-btn>

                <!-- Date Display -->
                <div class="text-subtitle-1 font-weight-medium ml-4">
                    {{ currentDateRange }}
                </div>
            </div>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
            <v-row no-gutters style="height: calc(100vh - 200px);">
                <!-- Unscheduled Tasks Sidebar -->
                <v-col cols="12" md="3" class="border-right">
                    <div class="unscheduled-tasks-container pa-3">
                        <div class="d-flex align-center justify-space-between mb-3">
                            <div class="d-flex align-center">
                                <v-icon small color="grey darken-1" class="mr-2">mdi-clock-outline</v-icon>
                                <span class="text-subtitle-2 font-weight-medium grey--text text--darken-2">
                                    Unscheduled Tasks ({{ unscheduledTasks.length }})
                                </span>
                            </div>
                            <v-btn
                                x-small
                                color="error"
                                outlined
                                @click="resetScheduleDialog = true"
                                :disabled="scheduledTasks.length === 0"
                            >
                                <v-icon x-small left>mdi-refresh</v-icon>
                                Reset
                            </v-btn>
                        </div>
                        <v-divider class="mb-3" />
                        
                        <!-- Scrollable task list -->
                        <div class="unscheduled-tasks-list">
                            <div
                                v-for="task in unscheduledTasks"
                                :key="task.id"
                                :data-task-id="task.id"
                                class="unscheduled-task-item mb-2 pa-2"
                                :class="{ 'task-loading': isTaskTemporary(task) }"
                                :style="{ borderLeftColor: getUnscheduledTaskColor(task) }"
                                :draggable="!isTaskTemporary(task)"
                                @dragstart="handleTaskDragStart($event, task)"
                                @dragend="handleTaskDragEnd($event)"
                            >
                                <div class="d-flex align-center mb-1">
                                    <v-icon small class="mr-1" :color="isTaskTemporary(task) ? 'grey lighten-1' : 'grey'">
                                        {{ isTaskTemporary(task) ? 'mdi-loading mdi-spin' : 'mdi-drag-vertical' }}
                                    </v-icon>
                                    <span class="text-caption font-weight-medium">
                                        {{ isRecurringTask(task) ? '🔄 ' : '' }}{{ task.name }}
                                    </span>
                                </div>
                                <div class="d-flex align-center justify-space-between">
                                    <v-chip
                                        x-small
                                        :color="getPriorityColor(task.priority_id)"
                                        text-color="white"
                                        class="mr-1"
                                    >
                                        {{ getPriorityName(task.priority_id) }}
                                    </v-chip>
                                    <span v-if="task.due_date" class="text-caption grey--text">
                                        Due: {{ formatDueDate(task.due_date) }}
                                    </span>
                                </div>
                            </div>
                            
                            <div v-if="unscheduledTasks.length === 0" class="text-center py-8">
                                <v-icon large color="grey lighten-1">mdi-check-circle-outline</v-icon>
                                <p class="text-caption grey--text mt-2">All tasks scheduled!</p>
                            </div>
                        </div>
                    </div>
                </v-col>

                <!-- Calendar -->
                <v-col cols="12" md="9">
                    <div class="pa-4 calendar-container">
                        <FullCalendar
                            ref="fullCalendar"
                            :options="calendarOptions"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-card-text>

        <!-- Reset Schedule Confirmation Dialog -->
        <v-dialog v-model="resetScheduleDialog" max-width="500">
            <v-card>
                <v-card-title class="headline error--text">
                    <v-icon left color="error">mdi-alert</v-icon>
                    Reset Schedule?
                </v-card-title>
                <v-card-text>
                    <p class="mb-2">
                        This will clear the start and end times for <strong>{{ scheduledTasks.length }}</strong> scheduled task(s).
                    </p>
                    <p class="mb-0 grey--text text--darken-1">
                        All scheduled tasks will be moved to the unscheduled list. This action cannot be undone.
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="resetScheduleDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="error"
                        @click="confirmResetSchedule"
                        :loading="resetScheduleLoading"
                    >
                        Reset Schedule
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Task Details Dialog (reuse existing TaskNavigationComponent) -->
        <TaskNavigationComponent
            :task_details_dialog="taskDetailsDialog"
            :can_close_task_details_dialog="canCloseTaskDetails"
            :users="users()"
            @updateTask="handleTaskUpdate"
            @closeTaskDetailsDialog="closeTaskDetails"
            @getTasks="refreshTasks"
        />
    </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import TaskNavigationComponent from '../modules/TaskNavigationComponent.vue';

export default {
    name: 'ScheduleView',
    components: {
        FullCalendar,
        TaskNavigationComponent,
    },
    inject: [
        'personalTasks',
        'assignedTasks',
        'users',
        'statuses',
        'priorities',
        'optimisticUpdateTask',
        'refreshTasks',
    ],
    props: {
        taskType: {
            type: String,
            default: 'personal',
            validator: value => ['personal', 'assigned', 'both'].includes(value)
        }
    },
    data() {
        return {
            currentView: 'timeGridWeek',
            currentDateRange: '',
            taskDetailsDialog: false,
            canCloseTaskDetails: false,
            calendarEvents: [], // Store events separately
            resetScheduleDialog: false,
            resetScheduleLoading: false,
            showWeekends: false, // Toggle for weekend visibility
            // Overlapping event display modes
            eventOverlapMode: 'limit-2', // 'overlap', 'stack', 'limit-2', 'limit-3', 'limit-4'
            eventMaxStack: 2, // Start with limit of 2 concurrent events
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
            get_statuses: 'tasks/get_statuses',
            get_priorities: 'tasks/get_priorities',
            get_selected_task: 'tasks/get_selected_task',
        }),
        allTasks() {
            const personal = this.personalTasks() || [];
            const assigned = this.assignedTasks() || [];
            
            switch (this.taskType) {
                case 'personal':
                    return personal;
                case 'assigned':
                    return assigned;
                case 'both':
                default:
                    return [...personal, ...assigned];
            }
        },
        // Tasks with start_time AND end_time set (scheduled)
        scheduledTasks() {
            return this.allTasks.filter(task => {
                // Must have BOTH start_time AND end_time to be considered scheduled
                return task.start_time && task.end_time;
            });
        },
        // Tasks without start_time and end_time (unscheduled)
        unscheduledTasks() {
            return this.allTasks.filter(task => {
                // Exclude tasks with ANY start_time or end_time, and exclude completed tasks (status_id 4)
                return !task.start_time && !task.end_time && task.status_id !== 4;
            });
        },
        calendarApi() {
            return this.$refs.fullCalendar?.getApi();
        },
        eventOverlapModeLabel() {
            const labels = {
                'overlap': 'Natural',
                'stack': 'Stack',
                'limit-2': 'Max 2',
                'limit-3': 'Max 3',
                'limit-4': 'Max 4'
            };
            return labels[this.eventOverlapMode] || 'Natural';
        },
        calendarOptions() {
            try {
                const options = {
                    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                    timeZone: this.$tz.display(),
                    initialView: 'timeGridWeek',
                    headerToolbar: false,
                    height: 'auto',
                    slotMinTime: '00:00:00',
                    slotMaxTime: '23:59:00',
                    allDaySlot: false,
                    editable: true,
                    eventResizableFromStart: true,
                    eventDurationEditable: true,
                    nowIndicator: true,
                    firstDay: 1, // Monday as first day of week
                    weekends: this.showWeekends, // Toggle weekends visibility
                    slotDuration: '00:30:00',
                    slotLabelInterval: '01:00',
                    // Overlapping event handling
                    slotEventOverlap: this.eventOverlapMode !== 'stack', // false = no overlap
                    eventMaxStack: this.eventMaxStack, // Limit stacked events
                    // FullCalendar does its own formatting, so the clock
                    // preference has to be handed to it separately.
                    slotLabelFormat: {
                    hour: this.$tz.ampm ? 'numeric' : '2-digit',
                    minute: '2-digit',
                    omitZeroMinute: false,
                    hour12: this.$tz.ampm,
                    meridiem: this.$tz.ampm ? 'short' : false
                },
                eventTimeFormat: {
                    hour: this.$tz.ampm ? 'numeric' : '2-digit',
                    minute: '2-digit',
                    hour12: this.$tz.ampm,
                    meridiem: this.$tz.ampm ? 'short' : false
                },
                events: this.calendarEvents,
                eventClick: this.handleEventClick,
                eventDrop: this.handleEventDrop,
                eventResize: this.handleEventResize,
                dateClick: this.handleDateClick,
                datesSet: this.handleDatesSet,
                eventClassNames: this.getEventClassNames,
                eventContent: this.renderEventContent,
                eventDidMount: this.handleEventDidMount,
                };
                return options;
            } catch (error) {
                console.error('[ScheduleView] Error computing calendar options:', error);
                throw error;
            }
        }
    },
    watch: {
        currentView(newView) {
            if (this.calendarApi) {
                this.calendarApi.changeView(newView);
            }
        },
        allTasks: {
            handler() {
                // Update calendar events when tasks change
                this.updateCalendarEvents();
            },
            deep: true
        },
        taskType: {
            handler() {
                // Refresh when task type filter changes
                this.updateCalendarEvents();
            }
        }
    },
    methods: {
        updateCalendarEvents() {
            try {
                // Update the events array directly
                const events = this.getCalendarEvents();
                this.calendarEvents = events;
            } catch (error) {
                console.error('[ScheduleView] Error updating calendar events:', error);
            }
        },
        getCalendarEvents() {
            try {
                // Only show scheduled tasks (with start_time AND end_time)
                const events = this.scheduledTasks
                    .map(task => {
                        const status = this.get_statuses.find(s => s.id === task.status_id);
                        const priority = this.get_priorities.find(p => p.id === task.priority_id);
                        
                        let startTime, endTime;
                        
                        // Priority 1: Use start_time and end_time if both are available
                        if (task.start_time && task.end_time) {
                            startTime = this.$date(task.start_time).toDate();
                            endTime = this.$date(task.end_time).toDate();
                        }
                        // Priority 2: Use start_time with allocated_time
                        else if (task.start_time && task.allocated_time) {
                            startTime = this.$date(task.start_time).toDate();
                            const [hours, minutes] = task.allocated_time.split(':');
                            const durationMs = (parseInt(hours) * 60 + parseInt(minutes)) * 60 * 1000;
                            endTime = new Date(startTime.getTime() + durationMs);
                        }
                        // Priority 3: Use start_time with default 1 hour duration
                        else if (task.start_time) {
                            startTime = this.$date(task.start_time).toDate();
                            endTime = this.$date(startTime).add(1, 'hour').toDate();
                        }
                        // Fallback: Use current time (shouldn't reach here due to filter)
                        else {
                            const now = this.$date();
                            startTime = now.toDate();
                            endTime = now.add(1, 'hour').toDate();
                        }

                        return {
                            id: task.id,
                            title: task.name,
                            start: startTime,
                            end: endTime,
                            allDay: false,
                            backgroundColor: this.getTaskColor(task, status, priority),
                            borderColor: this.getTaskBorderColor(task, status),
                            textColor: this.getTaskTextColor(task, status),
                            extendedProps: {
                                task: task,
                                status: status,
                                priority: priority,
                                assignees: task.assignees || [],
                                company: task.company,
                                isGhost: false, // Mark as main task (not ghost)
                                isRecurring: this.isRecurringTask(task) // Mark if this is a recurring task
                            }
                        };
                    });

                // Add ghost events for recurring tasks
                const ghostEvents = [];
                this.scheduledTasks.forEach(task => {
                    if (this.isRecurringTask(task)) {
                        const taskGhostEvents = this.generateRecurringEvents(task);
                        ghostEvents.push(...taskGhostEvents);
                    }
                });

                // Combine main events and ghost events
                return [...events, ...ghostEvents];
            } catch (error) {
                console.error('Error fetching calendar events:', error);
                return [];
            }
        },

        hasTimeComponent(dateString) {
            // Check if date string includes time (has colons)
            return dateString && dateString.includes(':');
        },

        getTaskColor(task, status, priority) {
            // Priority-based coloring with status opacity
            const priorityColors = {
                1: '#9C27B0', // Urgent - Purple
                2: '#F44336', // High - Red
                3: '#FF9800', // Medium - Orange
                4: '#4CAF50', // Low - Green
                null: '#9E9E9E'    // None - Grey
            };

            let baseColor = priorityColors[priority?.id] || '#2196F3'; // Default blue

            // Adjust opacity for completed tasks
            if (status?.id === 4) {
                return this.adjustColorOpacity(baseColor, 0.4);
            }

            return baseColor;
        },

        getTaskBorderColor(task, status) {
            if (status?.id === 4) return '#9E9E9E'; // Completed
            if (this.isTaskOverdue(task)) return '#D32F2F'; // Overdue
            return 'transparent';
        },

        getTaskTextColor(task, status) {
            return status?.id === 4 ? '#757575' : '#FFFFFF';
        },

        adjustColorOpacity(hex, opacity) {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        },

        isTaskOverdue(task) {
            if (!task.due_date) return false;
            if (task.status_id === 4) return false; // Completed
            return this.$date(task.due_date).isBefore(this.$date());
        },

        getEventClassNames(arg) {
            const classes = ['calendar-event'];
            const status = arg.event.extendedProps.status;
            const task = arg.event.extendedProps.task;
            const isGhost = arg.event.extendedProps.isGhost;
            const isRecurring = arg.event.extendedProps.isRecurring;

            if (status?.id === 4) classes.push('event-completed');
            if (this.isTaskOverdue(task)) classes.push('event-overdue');
            if (isGhost) classes.push('event-ghost');
            if (isRecurring) classes.push('event-recurring');

            return classes;
        },

        renderEventContent(arg) {
            const status = arg.event.extendedProps.status;
            const assignees = arg.event.extendedProps.assignees;
            const task = arg.event.extendedProps.task;
            const isGhost = arg.event.extendedProps.isGhost;
            const isRecurring = arg.event.extendedProps.isRecurring;
            const ghostDate = arg.event.extendedProps.ghostDate;

            // Get assignee names
            const assigneeNames = assignees.length > 0 
                ? assignees.map(a => a.user?.name || 'Unknown').join(', ')
                : '';

            // Format due date
            const dueDate = task.due_date ? this.$date(task.due_date).format('MMM D') : '';

            // Escape the title for use in data attribute
            const escapedTitle = this.escapeHtml(arg.event.title);

            // Determine recurring icon and ghost indicator
            const recurringIcon = isRecurring ? '<span style="font-size: 0.7rem; margin-right: 4px;" title="Recurring Task">🔄</span>' : '';
            const ghostIndicator = isGhost ? `<span style="font-size: 0.7rem; opacity: 0.8; margin-left: 4px; font-style: italic;" title="Recurring on ${ghostDate}">(recurring)</span>` : '';
            
            // Custom HTML for event with checkbox and improved layout
            return {
                html: `
                    <div class="event-content" style="padding: 4px;">
                        <div style="display: flex; align-items: flex-start; gap: 4px;">
                            ${isGhost ? '' : `
                                <input 
                                    type="checkbox" 
                                    class="task-checkbox" 
                                    data-task-id="${task.id}"
                                    ${status?.id === 4 ? 'checked' : ''}
                                    style="margin-top: 2px; cursor: pointer; flex-shrink: 0;"
                                />
                            `}
                            <div style="flex: 1; min-width: 0;">
                                <div 
                                    class="event-title" 
                                    title="${escapedTitle}${isGhost ? ' (Recurring on ' + ghostDate + ')' : ''}"
                                    style="font-weight: 500; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; word-break: break-word; margin-bottom: 2px; cursor: default; line-height: 1.2;"
                                >
                                    ${recurringIcon}${escapedTitle}${ghostIndicator}
                                </div>
                                <div class="event-time" style="font-size: 0.7rem; opacity: 0.9; margin-bottom: 2px;">
                                    ${arg.timeText}${isGhost ? ' (Recurring)' : ''}
                                </div>
                                ${status ? `<div class="event-status" style="font-size: 0.7rem; opacity: 0.85; margin-bottom: 2px;">Status: ${status.name}</div>` : ''}
                                ${dueDate && !isGhost ? `<div class="event-due-date" style="font-size: 0.7rem; opacity: 0.85; margin-bottom: 2px;">Due: ${dueDate}</div>` : ''}
                                ${assigneeNames ? `<div class="event-assignees" style="font-size: 0.7rem; opacity: 0.8; font-style: italic;">${this.escapeHtml(assigneeNames)}</div>` : ''}
                            </div>
                        </div>
                    </div>
                `
            };
        },

        getAssigneeInitials(assignees) {
            return assignees
                .slice(0, 3)
                .map(a => this.getInitials(a.user?.name || 'U'))
                .join(', ');
        },

        getInitials(name) {
            return name.split(' ').map(n => n[0]).join('').toUpperCase();
        },

        escapeHtml(text) {
            const map = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#039;'
            };
            return text.replace(/[&<>"']/g, m => map[m]);
        },

        // Called when each event is mounted to DOM
        handleEventDidMount(info) {
            // Find the checkbox in the event element and attach click listener
            const checkbox = info.el.querySelector('.task-checkbox');
            if (checkbox) {
                checkbox.addEventListener('click', async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    await this.toggleTaskCompletion(info.event.extendedProps.task);
                });
            }
        },

        async handleEventClick(info) {
            // Check if the click was on the checkbox
            if (info.jsEvent.target.classList.contains('task-checkbox')) {
                info.jsEvent.preventDefault();
                info.jsEvent.stopPropagation();
                return; // Checkbox click is handled separately
            }

            const task = info.event.extendedProps.task;
            const isGhost = info.event.extendedProps.isGhost;
            const ghostDate = info.event.extendedProps.ghostDate;

            // For ghost events, show the main task details but with ghost indicator
            if (isGhost) {
                this.$store.dispatch('tasks/set_selected_task', {
                    ...task,
                    _ghostOccurrence: ghostDate, // Add ghost occurrence info
                    _isGhostView: true // Flag to indicate this is a ghost view
                });
                this.$toast.info(`Viewing recurring task occurrence for ${this.$date(ghostDate).format('MMM D, YYYY')}`);
            } else {
                this.$store.dispatch('tasks/set_selected_task', task);
            }

            this.taskDetailsDialog = true;
            setTimeout(() => {
                this.canCloseTaskDetails = true;
            }, 100);
        },

        // Handle checkbox click to toggle task completion
        async handleCheckboxClick(event) {
            if (!event.target.classList.contains('task-checkbox')) return;

            const taskId = parseInt(event.target.getAttribute('data-task-id'));
            const task = this.allTasks.find(t => t.id === taskId);

            if (!task) return;

            event.stopPropagation();
            await this.toggleTaskCompletion(task);
        },

        // Toggle task completion status (used by both checkbox and event click)
        async toggleTaskCompletion(task) {
            // Determine new status (4 = completed, or revert to previous status)
            const newStatusId = task.status_id === 4 ? 1 : 4; // Toggle between completed and open

            try {
                await this.optimisticUpdateTask(
                    task.id,
                    { status_id: newStatusId },
                    async () => {
                        const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, {
                            company_id: task.company_id,
                            status_id: newStatusId
                        });
                        if (!response.data.response) {
                            throw new Error(response.data.message);
                        }
                        return response.data;
                    }
                );

                this.$toast.success(newStatusId === 4 ? 'Task completed' : 'Task reopened');
                this.updateCalendarEvents();
            } catch (error) {
                console.error('Error updating task status:', error);
                this.$toast.error('Failed to update task');
            }
        },

        // Check if task has a temporary ID (not yet saved to backend)
        isTaskTemporary(task) {
            return task && task.id && String(task.id).startsWith('temp_');
        },

        // Check if task is a recurring task with valid recurrence data
        isRecurringTask(task) {
            return task && 
                   task.status_id === 7 && // Recurring status
                   task.recurrence_days && 
                   Array.isArray(task.recurrence_days) && 
                   task.recurrence_days.length > 0 &&
                   task.recurrence_interval && 
                   task.recurrence_interval > 0;
        },

        // Generate recurring ghost events for a recurring task
        generateRecurringEvents(task) {
            if (!this.isRecurringTask(task) || !task.start_time) {
                return []; // No ghost events if not recurring or no start_time set
            }

            const ghostEvents = [];
            const taskStartTime = this.$date(task.start_time);
            const taskEndTime = this.$date(task.end_time || taskStartTime.add(1, 'hour'));
            const duration = taskEndTime.diff(taskStartTime, 'minute');
            const today = this.$date().startOf('day'); // Current date for comparison

            // Use series_start_date as the baseline for recurring pattern, fallback to start_time
            const seriesStartDate = task.series_start_date 
                ? this.$date(task.series_start_date) 
                : taskStartTime.startOf('day');

            // Calculate series end date (use series_end_date or default to 4 weeks from series start)
            const seriesEnd = task.series_end_date 
                ? this.$date(task.series_end_date) 
                : seriesStartDate.add(4, 'week');

            // Convert recurrence_days to JavaScript day format (0=Sunday, 1=Monday, etc.)
            // TaskCreationDialog uses: Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6, Sun=0
            const recurringDays = task.recurrence_days;
            
            // Start from the week containing the series_start_date
            let currentWeek = seriesStartDate.startOf('week'); // Start of the week containing series_start_date
            let weekCount = 0;
            const maxWeeks = 8; // Safety limit to prevent infinite loops

            while (currentWeek.isBefore(seriesEnd) && weekCount < maxWeeks) {
                // For each recurring day in the current week
                recurringDays.forEach(dayOfWeek => {
                    // Convert TaskCreationDialog day format to dayjs day format
                    const dayjsDay = dayOfWeek === 0 ? 0 : dayOfWeek; // Sunday stays 0, others stay same
                    const recurringDate = currentWeek.day(dayjsDay);
                    
                    // Skip if this date is before the series start, after series end, or before today
                    if (recurringDate.isBefore(seriesStartDate.startOf('day')) || 
                        recurringDate.isAfter(seriesEnd) ||
                        recurringDate.isBefore(today)) {
                        return;
                    }

                    // Skip if this is the same date as the main task (avoid duplicating the main task)
                    if (recurringDate.isSame(taskStartTime, 'day')) {
                        return;
                    }

                    // Create ghost event for this recurring date
                    // Use the same time of day as the main task's start_time
                    // recurringDate may be derived from a date-only series field
                    // (which is never zone-shifted), so take only its calendar
                    // date and re-anchor the time as DISPLAY-zone wall clock —
                    // the same wall clock the main task shows.
                    const ghostStartTime = this.$dateApi(
                        `${recurringDate.format('YYYY-MM-DD')} ${taskStartTime.format('HH:mm:ss')}`
                    );

                    const ghostEndTime = ghostStartTime.add(duration, 'minute');

                    ghostEvents.push({
                        id: `recurring-${task.id}-${recurringDate.format('YYYY-MM-DD')}`,
                        title: task.name,
                        start: ghostStartTime.toDate(),
                        end: ghostEndTime.toDate(),
                        allDay: false,
                        backgroundColor: this.adjustColorOpacity(
                            this.getTaskColor(task, 
                                this.get_statuses.find(s => s.id === task.status_id),
                                this.get_priorities.find(p => p.id === task.priority_id)
                            ), 
                            0.6 // Make ghost events semi-transparent
                        ),
                        borderColor: 'transparent',
                        textColor: '#FFFFFF',
                        extendedProps: {
                            task: task, // Reference to original task
                            isGhost: true, // Mark as ghost event
                            ghostDate: recurringDate.format('YYYY-MM-DD'), // Track which occurrence this is
                            status: this.get_statuses.find(s => s.id === task.status_id),
                            priority: this.get_priorities.find(p => p.id === task.priority_id),
                            assignees: task.assignees || [],
                            company: task.company,
                        },
                        // Disable dragging and resizing for ghost events
                        editable: false,
                        startEditable: false,
                        durationEditable: false
                    });
                });

                // Move to next week based on recurrence_interval
                currentWeek = currentWeek.add(task.recurrence_interval, 'week');
                weekCount++;
            }

            return ghostEvents;
        },

        // Handle drag start from unscheduled task list
        handleTaskDragStart(event, task) {
            // Don't allow dragging tasks with temporary IDs
            if (this.isTaskTemporary(task)) {
                event.preventDefault();
                this.$toast.info('Please wait for the task to finish saving before scheduling');
                return;
            }

            // Store task ID for drop handler
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', task.id.toString());
            // Add visual feedback
            event.target.style.opacity = '0.5';
        },

        // Handle drag end to reset opacity
        handleTaskDragEnd(event) {
            event.target.style.opacity = '1';
        },

        // Handle calendar container drop events
        handleCalendarDragOver(event) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        },

        async handleCalendarDrop(event) {
            event.preventDefault();
            
            try {
                // Get task ID from drag data
                const taskId = event.dataTransfer.getData('text/plain');
                const task = this.unscheduledTasks.find(t => t.id.toString() === taskId);
                
                if (!task) {
                    this.$toast.error('Task not found');
                    return;
                }

                // Get calendar API to use its built-in date calculation
                const calendarApi = this.calendarApi;
                if (!calendarApi) {
                    this.$toast.error('Calendar not ready');
                    return;
                }

                // Get the element at the drop position
                const dropX = event.clientX;
                const dropY = event.clientY;
                
                // Use FullCalendar's built-in method to get date from coordinates
                // We need to find the closest time grid slot
                const calendarEl = this.$refs.fullCalendar.$el;
                
                // Get all day columns
                const dayColumns = calendarEl.querySelectorAll('.fc-day[data-date]');
                let targetDate = null;
                let targetColumn = null;
                
                // Find which day column was dropped on
                for (const col of dayColumns) {
                    const rect = col.getBoundingClientRect();
                    if (dropX >= rect.left && dropX <= rect.right) {
                        targetDate = col.getAttribute('data-date');
                        targetColumn = col;
                        break;
                    }
                }
                
                if (!targetDate) {
                    this.$toast.error('Could not determine drop date');
                    return;
                }
                
                // Now find the time slot within that column
                const timeSlots = calendarEl.querySelectorAll('.fc-timegrid-slot[data-time]');
                let targetTime = '09:00:00'; // Default to 9 AM
                
                for (const slot of timeSlots) {
                    const rect = slot.getBoundingClientRect();
                    // Check if drop is within this slot's vertical range
                    if (dropY >= rect.top && dropY < rect.bottom) {
                        // Check if slot is in the same column
                        const slotParent = slot.closest('.fc-timegrid-col');
                        if (slotParent && targetColumn && slotParent.contains(targetColumn.querySelector('.fc-timegrid-col-frame'))) {
                            targetTime = slot.getAttribute('data-time');
                            break;
                        }
                        // Alternative: check by left/right bounds
                        if (dropX >= rect.left && dropX <= rect.right) {
                            targetTime = slot.getAttribute('data-time');
                            break;
                        }
                    }
                }
                
                // Combine date and time. targetDate/targetTime are read off the
                // calendar grid, so they are wall-clock in the DISPLAY zone —
                // $dateApi is the entry point that parses a naive string that way.
                const dropDate = this.$dateApi(`${targetDate} ${targetTime}`).toDate();
                
                const startTime = this.formatDateForAPI(dropDate);
                const endDate = this.$date(dropDate).add(1, 'hour').toDate();
                const endTime = this.formatDateForAPI(endDate);
                const allocatedTime = '01:00:00';

                // Update task (keep due_date independent)
                await this.optimisticUpdateTask(
                    task.id,
                    {
                        start_time: startTime,
                        end_time: endTime,
                        allocated_time: allocatedTime
                    },
                    async () => {
                        const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, {
                            company_id: task.company_id,
                            start_time: startTime,
                            end_time: endTime,
                            allocated_time: allocatedTime
                        });
                        if (!response.data.response) {
                            throw new Error(response.data.message);
                        }
                        return response.data;
                    }
                );
                
                this.$toast.success('Task scheduled');
                this.updateCalendarEvents();
            } catch (error) {
                console.error('Error scheduling task:', error);
                this.$toast.error('Failed to schedule task');
            }
        },

        async handleEventDrop(info) {
            const task = info.event.extendedProps.task;
            const isGhost = info.event.extendedProps.isGhost;
            
            // Prevent dragging ghost events
            if (isGhost) {
                this.$toast.info('Cannot reschedule recurring instances. Edit the main recurring task instead.');
                info.revert();
                return;
            }

            const newStart = info.event.start;
            const newEnd = info.event.end;

            // Validate dates
            if (!newStart || !newEnd) {
                this.$toast.error('Invalid date range');
                info.revert();
                return;
            }

            // Format start_time and end_time for API
            const newStartTime = this.formatDateForAPI(newStart);
            const newEndTime = this.formatDateForAPI(newEnd);
            
            // Calculate allocated_time from the duration using dayjs
            const start = this.$date(newStart);
            const end = this.$date(newEnd);
            const durationMinutes = end.diff(start, 'minute');
            
            // Validate duration is positive
            if (durationMinutes <= 0) {
                this.$toast.error('End time must be after start time');
                info.revert();
                return;
            }
            
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            const newAllocatedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

            try {
                await this.optimisticUpdateTask(
                    task.id,
                    {
                        start_time: newStartTime,
                        end_time: newEndTime,
                        allocated_time: newAllocatedTime
                    },
                    async () => {
                        const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, {
                            company_id: task.company_id,
                            start_time: newStartTime,
                            end_time: newEndTime,
                            allocated_time: newAllocatedTime
                        });
                        if (!response.data.response) {
                            throw new Error(response.data.message);
                        }
                        return response.data;
                    }
                );
                this.$toast.success('Task rescheduled');
            } catch (error) {
                this.$toast.error('Failed to reschedule task');
                info.revert(); // Revert calendar change
            }
        },

        async handleEventResize(info) {
            const task = info.event.extendedProps.task;
            const isGhost = info.event.extendedProps.isGhost;
            
            // Prevent resizing ghost events
            if (isGhost) {
                this.$toast.info('Cannot resize recurring instances. Edit the main recurring task instead.');
                info.revert();
                return;
            }

            // Update start_time, end_time, and allocated_time when resizing
            const newEnd = info.event.end;
            const newStart = info.event.start;

            // Validate dates
            if (!newStart || !newEnd) {
                this.$toast.error('Invalid date range');
                info.revert();
                return;
            }

            // Format start_time and end_time for API
            const newStartTime = this.formatDateForAPI(newStart);
            const newEndTime = this.formatDateForAPI(newEnd);
            
            // Calculate allocated_time from the new duration using dayjs
            const start = this.$date(newStart);
            const end = this.$date(newEnd);
            const durationMinutes = end.diff(start, 'minute');
            
            // Validate duration is positive
            if (durationMinutes <= 0) {
                this.$toast.error('End time must be after start time');
                info.revert();
                return;
            }
            
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            const newAllocatedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;

            try {
                await this.optimisticUpdateTask(
                    task.id,
                    { 
                        start_time: newStartTime,
                        end_time: newEndTime,
                        allocated_time: newAllocatedTime
                    },
                    async () => {
                        const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, {
                            company_id: task.company_id,
                            start_time: newStartTime,
                            end_time: newEndTime,
                            allocated_time: newAllocatedTime
                        });
                        if (!response.data.response) {
                            throw new Error(response.data.message);
                        }
                        return response.data;
                    }
                );
                this.$toast.success('Task duration updated');
            } catch (error) {
                this.$toast.error('Failed to update task duration');
                info.revert();
            }
        },

        handleDateClick() {
            // Optionally: Quick create task at clicked time
            // You could emit event to parent or open quick create dialog
        },

        handleDatesSet(info) {
            // Update displayed date range
            this.updateDateRange(info.view);
        },

        updateDateRange(view) {
            const start = this.$date(view.currentStart);
            const end = this.$date(view.currentEnd);

            if (view.type === 'timeGridDay') {
                this.currentDateRange = start.format('MMMM D, YYYY');
            } else if (view.type === 'timeGridWeek') {
                if (start.month() === end.month()) {
                    this.currentDateRange = `${start.format('MMMM D')} - ${end.format('D, YYYY')}`;
                } else {
                    this.currentDateRange = `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
                }
            }
        },

        formatDateForAPI(date) {
            // Handle both Date objects and datetime-local strings (YYYY-MM-DDTHH:mm).
            // Uses $dateApi so the value written to the API is in the company
            // baseline zone regardless of what the viewer's display zone is.
            if (!date) return null;
            return this.$dateApi(date).format('YYYY-MM-DD HH:mm:ss');
        },

        // Calendar navigation
        calendarPrev() {
            this.calendarApi?.prev();
        },

        calendarNext() {
            this.calendarApi?.next();
        },

        calendarToday() {
            this.calendarApi?.today();
        },

        // Task details handlers
        async handleTaskUpdate() {
            // Refresh calendar after task update
            this.$nextTick(() => {
                this.calendarApi?.refetchEvents();
            });
        },

        closeTaskDetails() {
            this.taskDetailsDialog = false;
            this.canCloseTaskDetails = false;
            this.$store.dispatch('tasks/set_selected_task', {});
        },

        // Helper methods for unscheduled tasks
        getUnscheduledTaskColor(task) {
            const priority = this.get_priorities.find(p => p.id === task.priority_id);
            const priorityColors = {
                1: '#9C27B0', // Urgent - Purple
                2: '#F44336', // High - Red
                3: '#FF9800', // Medium - Orange
                4: '#4CAF50', // Low - Green
                null: '#9E9E9E'    // None - Grey
            };
            return priorityColors[priority?.id] || '#9E9E9E'; // Default grey
        },

        getPriorityColor(priorityId) {
            const colors = {
                1: 'purple',
                2: 'red',
                3: 'orange',
                4: 'green',
            };
            return colors[priorityId] || 'blue';
        },

        getPriorityName(priorityId) {
            const priority = this.get_priorities.find(p => p.id === priorityId);
            return priority?.name || 'Normal';
        },

        formatDueDate(dueDate) {
            if (!dueDate) return '';
            return this.$date(dueDate).format('MMM D');
        },

        async confirmResetSchedule() {
            if (this.scheduledTasks.length === 0) {
                this.$toast.info('No scheduled tasks to reset');
                this.resetScheduleDialog = false;
                return;
            }

            this.resetScheduleLoading = true;

            try {
                // Get all scheduled task IDs
                const taskIds = this.scheduledTasks.map(task => task.id);

                // Optimistic update: Clear start_time and end_time for all scheduled tasks
                this.scheduledTasks.forEach(task => {
                    const taskInArray = this.allTasks.find(t => t.id === task.id);
                    if (taskInArray) {
                        taskInArray.start_time = null;
                        taskInArray.end_time = null;
                        taskInArray.allocated_time = null;
                    }
                });

                // Update calendar events immediately
                this.updateCalendarEvents();

                // Make API call to bulk update
                const response = await this.$axios.post('api/tasks/bulk_update', {
                    task_ids: taskIds,
                    updates: {
                        start_time: null,
                        end_time: null,
                        allocated_time: null
                    }
                });

                if (!response.data.response) {
                    throw new Error(response.data.message || 'Failed to reset schedule');
                }

                this.$toast.success(`Schedule reset for ${taskIds.length} task(s)`);
                this.resetScheduleDialog = false;
                
                // Refresh tasks from provider to ensure consistency
                await this.refreshTasks();

            } catch (error) {
                console.error('Error resetting schedule:', error);
                this.$toast.error('Failed to reset schedule');
                
                // Rollback: Restore original data
                // Note: In a production system with optimisticUpdateTask pattern,
                // the rollback would be automatic. For bulk operations, we handle it manually.
                await this.refreshTasks();
            } finally {
                this.resetScheduleLoading = false;
            }
        },

        // Event overlap mode handling
        setEventOverlapMode(mode) {
            this.eventOverlapMode = mode;
            
            // Update eventMaxStack based on mode
            if (mode === 'overlap') {
                this.eventMaxStack = null; // No limit
            } else if (mode === 'stack') {
                this.eventMaxStack = null; // No limit, but side-by-side
            } else if (mode.startsWith('limit-')) {
                const limit = parseInt(mode.split('-')[1]);
                this.eventMaxStack = limit;
            }
            
            // Force calendar to re-render with new settings
            this.$nextTick(() => {
                if (this.calendarApi) {
                    // Update options dynamically
                    this.calendarApi.setOption('slotEventOverlap', this.eventOverlapMode !== 'stack');
                    this.calendarApi.setOption('eventMaxStack', this.eventMaxStack);
                }
            });
        },
    },
    mounted() {
        try {
            // Load initial events
            this.updateCalendarEvents();
            
            // Initialize date range display after calendar is ready
            this.$nextTick(() => {
                if (this.calendarApi) {
                    this.updateDateRange(this.calendarApi.view);
                } else {
                    console.error('[ScheduleView] Calendar API not available in nextTick');
                }
                
                // Add native drop listeners to calendar
                const calendarEl = this.$refs.fullCalendar?.$el;
                if (calendarEl) {
                    calendarEl.addEventListener('dragover', this.handleCalendarDragOver);
                    calendarEl.addEventListener('drop', this.handleCalendarDrop);
                    // Add checkbox click handler
                    calendarEl.addEventListener('click', this.handleCheckboxClick);
                }
            });
        } catch (error) {
            console.error('[ScheduleView] Error in mounted lifecycle:', error);
        }
    },
    
    beforeDestroy() {
        // Clean up event listeners
        const calendarEl = this.$refs.fullCalendar?.$el;
        if (calendarEl) {
            calendarEl.removeEventListener('dragover', this.handleCalendarDragOver);
            calendarEl.removeEventListener('drop', this.handleCalendarDrop);
            calendarEl.removeEventListener('click', this.handleCheckboxClick);
        }
    }
};
</script>

<style lang="scss" scoped>
// Border utility
.border-right {
    border-right: 1px solid #e0e0e0;
}

// Calendar container
.calendar-container {
    max-height: calc(100vh - 250px);
    overflow-y: auto;
    overflow-x: hidden;
    
    // Custom scrollbar
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
        
        &:hover {
            background: #555;
        }
    }
}

// Unscheduled Tasks Sidebar
.unscheduled-tasks-container {
    height: 100%;
    background-color: #fafafa;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.unscheduled-tasks-list {
    flex: 1;
    max-height: calc(100vh - 300px);
    overflow-y: auto;
    overflow-x: hidden;
    
    // Custom scrollbar
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 3px;
        
        &:hover {
            background: #555;
        }
    }
}

.unscheduled-task-item {
    background: white;
    border-radius: 4px;
    border-left: 4px solid;
    cursor: move;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    
    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }
    
    &:active {
        opacity: 0.7;
        cursor: grabbing;
    }
    
    &.task-loading {
        cursor: not-allowed;
        opacity: 0.6;
        
        &:hover {
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            transform: none;
        }
        
        &:active {
            cursor: not-allowed;
        }
    }
}

// FullCalendar custom styling
:deep(.fc) {
    // Override FullCalendar defaults to match Vuetify theme
    font-family: 'Roboto', sans-serif;

    .fc-toolbar-title {
        font-size: 1.25rem;
        font-weight: 500;
    }

    .fc-button {
        background-color: #1976d2;
        border-color: #1976d2;
        text-transform: none;
        
        &:hover {
            background-color: #1565c0;
            border-color: #1565c0;
        }
    }

    .fc-button-primary:disabled {
        background-color: #e0e0e0;
        border-color: #e0e0e0;
    }

    .fc-timegrid-slot {
        height: 3em;
    }

    .fc-timegrid-slot-label {
        font-size: 0.875rem;
        color: #666;
    }

    .fc-col-header-cell {
        background-color: #f5f5f5;
        font-weight: 500;
        padding: 8px 4px;
    }

    .fc-daygrid-day-number {
        font-size: 0.875rem;
        padding: 4px;
    }

    .fc-event {
        border-radius: 4px;
        padding: 2px 4px;
        font-size: 0.875rem;
        cursor: pointer;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.85;
        }
    }

    .fc-timegrid-event {
        border-left-width: 3px;
    }

    // Custom event content styling
    .event-content {
        padding: 2px;
        line-height: 1.3;
    }

    .event-title {
        font-weight: 500;
        cursor: default;
        line-height: 1.2;
    }

    .event-time {
        font-size: 0.75rem;
        font-weight: 600;
        margin-bottom: 2px;
    }

    .event-status,
    .event-due-date,
    .event-assignees {
        font-size: 0.7rem;
        line-height: 1.2;
    }

    .task-checkbox {
        appearance: auto;
        -webkit-appearance: checkbox;
        -moz-appearance: checkbox;
        width: 14px;
        height: 14px;
        
        &:hover {
            transform: scale(1.1);
        }
    }

    // Status-based styling
    .event-completed {
        opacity: 0.6;
        text-decoration: line-through;
    }

    .event-overdue {
        border-left-color: #D32F2F !important;
        border-left-width: 4px !important;
    }

    // Recurring task styling
    .event-recurring {
        position: relative;
        
        &::before {
            content: '';
            position: absolute;
            top: 2px;
            right: 2px;
            width: 8px;
            height: 8px;
            background: rgba(255, 255, 255, 0.8);
            border-radius: 50%;
            border: 1px solid rgba(0, 0, 0, 0.2);
        }
    }

    // Ghost event styling
    .event-ghost {
        opacity: 0.7 !important;
        border-style: dashed !important;
        border-width: 2px !important;
        
        // Disable hover effects for ghost events
        &:hover {
            opacity: 0.7 !important;
            transform: none !important;
            cursor: default !important;
        }
    }

    // Current time indicator
    .fc-timegrid-now-indicator-line {
        border-color: #F44336;
        border-width: 2px;
    }

    .fc-timegrid-now-indicator-arrow {
        border-color: #F44336;
    }

    // Weekend styling
    .fc-day-sat,
    .fc-day-sun {
        background-color: #fafafa;
    }

    // More-link styling (for limited views)
    .fc-timegrid-more-link {
        background-color: #1976d2;
        color: white;
        border-radius: 3px;
        padding: 2px 4px;
        font-size: 0.7rem;
        font-weight: 500;
        text-align: center;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: #1565c0;
        }
    }

    // More-link popover styling
    .fc-popover {
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        border: 1px solid #e0e0e0;

        .fc-popover-header {
            background-color: #1976d2;
            color: white;
            padding: 8px 12px;
            border-radius: 8px 8px 0 0;
            font-weight: 500;
        }

        .fc-popover-close {
            color: white;
            opacity: 0.8;
            cursor: pointer;

            &:hover {
                opacity: 1;
            }
        }

        .fc-popover-body {
            padding: 8px;
            max-height: 300px;
            overflow-y: auto;
        }
    }
}

.gap-2 {
    gap: 8px;
}
</style>
